import type {
  SoundAsset,
  SoundBus,
  SoundEventMapping,
  SoundManifest,
  SoundSource,
  SoundVariation,
} from "./types.js";

const BUSES: readonly SoundBus[] = ["ui", "sfx", "music", "ambience"];
const SELECTS: readonly NonNullable<SoundEventMapping["select"]>[] = [
  "random",
  "shuffle",
  "sequence",
];

type Report = (issue: string) => void;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Own entries of a record, never `__proto__` (assigning it would replace the result's prototype). */
function ownEntries(record: Record<string, unknown>, where: string, report: Report) {
  return Object.keys(record).filter((key) => {
    if (key !== "__proto__") return true;
    report(`${where}: skipped a "__proto__" key`);
    return false;
  });
}

/** Copies `key` when it's a finite number that passes `accept`; reports and skips it otherwise. */
function copyNumber(
  from: Record<string, unknown>,
  to: Record<string, unknown>,
  key: string,
  where: string,
  report: Report,
  accept: (value: number) => boolean = () => true,
) {
  if (!(key in from) || from[key] === undefined) return;
  const value = from[key];
  if (typeof value === "number" && Number.isFinite(value) && accept(value)) {
    to[key] = value;
    return;
  }
  report(`${where}.${key}: dropped ${JSON.stringify(value) ?? String(value)}`);
}

const nonNegative = (value: number) => value >= 0;

function parseSource(input: unknown): SoundSource | null {
  if (!isRecord(input) || typeof input.url !== "string" || typeof input.type !== "string") {
    return null;
  }
  return { url: input.url, type: input.type };
}

function parseAsset(input: unknown, where: string, report: Report): SoundAsset | null {
  if (!isRecord(input) || !Array.isArray(input.sources)) {
    report(`${where}: dropped, not an asset with a list of sources`);
    return null;
  }
  const sources = input.sources.map(parseSource).filter((s): s is SoundSource => s !== null);
  if (sources.length < input.sources.length) report(`${where}: dropped a malformed source`);
  if (sources.length === 0) {
    report(`${where}: dropped, no usable source`);
    return null;
  }
  const asset: Record<string, unknown> = { sources };
  copyNumber(input, asset, "durationMs", where, report, nonNegative);
  copyNumber(input, asset, "lufs", where, report);
  if (isRecord(input.cues)) {
    const cues: Record<string, number> = {};
    for (const name of ownEntries(input.cues, `${where}.cues`, report)) {
      copyNumber(input.cues, cues, name, `${where}.cues`, report, nonNegative);
    }
    asset.cues = cues;
  }
  return asset as unknown as SoundAsset;
}

function parseVariation(input: unknown, where: string, report: Report): SoundVariation | null {
  if (!isRecord(input) || typeof input.asset !== "string") {
    report(`${where}: dropped, no asset name`);
    return null;
  }
  const variation: Record<string, unknown> = { asset: input.asset };
  copyNumber(input, variation, "weight", where, report, nonNegative);
  copyNumber(input, variation, "gainDb", where, report);
  copyNumber(input, variation, "startMs", where, report, nonNegative);
  copyNumber(input, variation, "endMs", where, report, nonNegative);
  return variation as unknown as SoundVariation;
}

function parseMapping(input: unknown, where: string, report: Report): SoundEventMapping | null {
  if (!isRecord(input) || !Array.isArray(input.variations)) {
    report(`${where}: dropped, no list of variations`);
    return null;
  }
  const mapping: Record<string, unknown> = {
    variations: input.variations
      .map((variation, index) => parseVariation(variation, `${where}.variations[${index}]`, report))
      .filter((v): v is SoundVariation => v !== null),
  };
  if (input.select !== undefined) {
    if (SELECTS.includes(input.select as never)) mapping.select = input.select;
    else report(`${where}.select: dropped ${JSON.stringify(input.select)}`);
  }
  if (input.bus !== undefined) {
    if (BUSES.includes(input.bus as never)) mapping.bus = input.bus;
    else report(`${where}.bus: dropped ${JSON.stringify(input.bus)}`);
  }
  copyNumber(input, mapping, "gainDb", where, report);
  copyNumber(input, mapping, "pitchJitterCents", where, report, nonNegative);
  copyNumber(input, mapping, "gainJitterDb", where, report, nonNegative);
  copyNumber(input, mapping, "maxVoices", where, report, (v) => Number.isInteger(v) && v >= 1);
  copyNumber(input, mapping, "cooldownMs", where, report, nonNegative);
  copyNumber(input, mapping, "delayMs", where, report, nonNegative);
  copyNumber(input, mapping, "fadeInMs", where, report, nonNegative);
  copyNumber(input, mapping, "fadeOutMs", where, report, nonNegative);
  if (typeof input.loop === "boolean") {
    mapping.loop = input.loop;
  } else if (input.loop !== undefined) {
    const loop = isRecord(input.loop) ? input.loop : {};
    const { startMs, endMs } = loop;
    if (
      typeof startMs === "number" &&
      typeof endMs === "number" &&
      Number.isFinite(startMs) &&
      Number.isFinite(endMs) &&
      startMs >= 0 &&
      endMs > startMs
    ) {
      mapping.loop = { startMs, endMs };
    } else {
      report(`${where}.loop: dropped ${JSON.stringify(input.loop)}`);
    }
  }
  return mapping as unknown as SoundEventMapping;
}

/**
 * A manifest as the engine can trust it. Theme JSON comes from outside the code (a dashboard, a
 * CDN), so each event and asset is checked once here: one that's malformed is dropped (a lower
 * layer then answers it), and a field that isn't a usable number is dropped (the default applies).
 * `report` hears about each, so a broken theme is visible rather than silent.
 */
export function parseSoundManifest(input: unknown, report: Report): SoundManifest {
  const manifest: SoundManifest = { schema: 1, assets: {}, events: {} };
  if (!isRecord(input)) {
    report("manifest: not an object, read as empty");
    return manifest;
  }
  if (isRecord(input.assets)) {
    for (const name of ownEntries(input.assets, "assets", report)) {
      const asset = parseAsset(input.assets[name], `assets.${name}`, report);
      if (asset) manifest.assets[name] = asset;
    }
  }
  if (isRecord(input.events)) {
    for (const id of ownEntries(input.events, "events", report)) {
      const mapping = parseMapping(input.events[id], `events.${id}`, report);
      if (mapping) manifest.events[id] = mapping;
    }
  }
  return manifest;
}
